import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount } from 'wagmi'
import { useRouter } from 'next/router'
import { useNativeTokenBalance } from '~/state/wallet/hooks'
import {
  useStEthBalance,
  useWstEthBalance,
  useWithdrawalRate,
  useWithdrawRequest,
  useWaitingTime,
  useWithdrawRequestGasFee,
  useWithdrawalReceiveAmount,
  useIsValidNetwork,
} from '../hooks'
import { TransactionCallbackStage } from '@lidofinance/lido-ethereum-sdk'
import { formatFormAmount, toBigNumber, gt } from '~/utils/numbers'
import MaxButton from '~/components/Button/MaxButton'
import TokenInput from '../components/TokenInput'
import PrimaryButton from '~/components/Button/PrimaryButton'
import OrderProgress from '../components/OrderProgress'
import NotificationBar from '~/components/NotificationBar'
import Tooltip from '~/components/Tooltip'
import Skeleton from '~/components/Skeleton'
import { ETH, stETH } from '~/constants/tokens'
import { WithdrawalFormProps, WithdrawToken } from './types'
import {
  StyledStakeForm,
  StyledFormGroup,
  StyledLabel,
  StyledBalanceInfo,
  StyledReceiveInfo,
  StyledInfoRow,
  StyledInfoLabel,
  StyledInfoValue,
} from '../styled'
import IconQuestionMark from '~/assets/images/question-mark-gray.svg'
import { ApprovalStatus } from '../types'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useIsDisableTrade } from '~/state/dex/hooks'
import { useGA } from '~/hooks'

const LidoWithdrawalSection: React.FC<WithdrawalFormProps> = ({
  amount,
  setAmount,
  getInitialToken,
  handleInputChange,
  handleBlur,
  setIsOpenTokenList,
  toggleWalletModal,
  toggleSwitchNetworkModal,
}) => {
  const { t } = useTranslation()
  const { trackClick } = useGA()
  const { address: account } = useAccount()
  const isWalletConnected = !!account
  const router = useRouter()
  const { token } = router.query

  const [
    currentStage,
    setCurrentStage,
  ] = useState<TransactionCallbackStage | null>(null)
  const [showOrderProgress, setShowOrderProgress] = useState(false)
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>('idle')

  const [selectedToken, setSelectedToken] = useState<BaseTokenlonToken>(
    getInitialToken(),
  )

  useEffect(() => {
    const newToken = getInitialToken()
    if (newToken.address !== selectedToken.address) {
      setSelectedToken(newToken)
    }
  }, [token, selectedToken.address])

  const isValidNetwork = useIsValidNetwork()

  const stEthBalance = useStEthBalance()
  const formattedStEthBalance = stEthBalance?.toDecimalsFixed()
  const wstEthBalance = useWstEthBalance()
  const formattedWstEthBalance = wstEthBalance?.toDecimalsFixed()
  const ethBalance = useNativeTokenBalance()
  const formattedEthBalance = ethBalance?.toDecimalsFixed()
  const isSelectedStETH = selectedToken.address === stETH.address
  const selectedTokenBalance = isSelectedStETH
    ? formattedStEthBalance
    : formattedWstEthBalance

  const isInsufficientBalance = useMemo(() => {
    return gt(amount, selectedTokenBalance)
  }, [amount, selectedTokenBalance])

  const {
    value: waitingTime,
    isValidating: isWaitingTimeValidating,
  } = useWaitingTime(amount)
  const { rate, isValidating: isRateValidating } = useWithdrawalRate(
    isSelectedStETH,
  )
  const {
    receiveAmount,
    isValidating: isReceiveAmountValidating,
  } = useWithdrawalReceiveAmount(isSelectedStETH, amount)
  const {
    request,
    isTokenLocked,
    isApprovalFlow,
    approveToken,
  } = useWithdrawRequest(
    amount,
    selectedToken.symbol as WithdrawToken,
    setCurrentStage,
    showOrderProgress,
    setShowOrderProgress,
    setAmount,
    approvalStatus,
    setApprovalStatus,
  )
  const {
    gasFee,
    gasFeeUSD,
    isValidating: isGasFeeValidating,
  } = useWithdrawRequestGasFee({
    token: selectedToken.symbol as WithdrawToken,
    isApprovalFlow,
  })

  const isInsufficientETHForGas = useMemo(() => {
    if (!amount || !formattedEthBalance || !gasFee) {
      return false
    }
    const ethBalanceBN = toBigNumber(formattedEthBalance)
    const gasFeeRequired = toBigNumber(gasFee)
    return gt(gasFeeRequired, ethBalanceBN)
  }, [amount, formattedEthBalance, gasFee])

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const handleMaxClick = () => {
    if (selectedTokenBalance !== amount && approvalStatus !== 'idle') {
      setApprovalStatus('idle')
    }
    setAmount(selectedTokenBalance)
  }

  const handleInputChangeWithReset = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value
    if (value !== amount && approvalStatus !== 'idle') {
      setApprovalStatus('idle')
    }
    handleInputChange(e)
  }

  const handleRequestWithdrawal = async () => {
    if (isTokenLocked && approvalStatus === 'idle') {
      await approveToken({
        amount,
        token: selectedToken.symbol as WithdrawToken,
      })
    } else {
      await request({ amount, token: selectedToken.symbol as WithdrawToken })
    }
  }

  const handleConnectWallet = () => {
    trackClick('webtl_walletconnect', {
      from: 'lido_withdraw',
    })
    toggleWalletModal()
  }

  const handleClick = () => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    } else if (!isWalletConnected) {
      handleConnectWallet()
    } else if (!isValidNetwork) {
      toggleSwitchNetworkModal()
    } else {
      handleRequestWithdrawal()
    }
  }

  const buttonText = useMemo(() => {
    if (!isWalletConnected) {
      return t('connect_wallet')
    }
    if (!isValidNetwork) {
      return t('switch_network')
    }
    if (isInsufficientBalance) {
      return t('preview_$symbol_insufficient_balance', {
        symbol: selectedToken.symbol,
      })
    }
    if (isInsufficientETHForGas) {
      return t('preview_$symbol_insufficient_balance', { symbol: 'ETH' })
    }
    if (
      currentStage !== null &&
      isApprovalFlow &&
      approvalStatus === 'approving'
    ) {
      return t('authorizing')
    }
    if (currentStage === null) {
      if (isTokenLocked && approvalStatus === 'idle') {
        return t('approve_token', { symbol: selectedToken.symbol })
      } else {
        return t('withdraw')
      }
    }
    return t('waiting_for_confirm')
  }, [
    isWalletConnected,
    isValidNetwork,
    currentStage,
    isTokenLocked,
    isApprovalFlow,
    isInsufficientBalance,
    isInsufficientETHForGas,
    selectedToken.symbol,
    approvalStatus,
    t,
  ])

  const buttonDisabled = useMemo(() => {
    return (
      isWalletConnected &&
      isValidNetwork &&
      (!Number(amount) ||
        isInsufficientBalance ||
        isInsufficientETHForGas ||
        currentStage === TransactionCallbackStage.SIGN)
    )
  }, [
    isWalletConnected,
    isValidNetwork,
    isInsufficientBalance,
    isInsufficientETHForGas,
    amount,
    currentStage,
  ])

  const showApproveButton = useMemo(
    () =>
      isTokenLocked &&
      approvalStatus === 'idle' &&
      !isInsufficientBalance &&
      !isInsufficientETHForGas,
    [
      isTokenLocked,
      approvalStatus,
      isInsufficientBalance,
      isInsufficientETHForGas,
    ],
  )

  return (
    <StyledStakeForm>
      <StyledFormGroup>
        {isWalletConnected && (
          <StyledLabel>
            <StyledBalanceInfo>
              {`${t('balance')}: ${formatFormAmount(
                selectedTokenBalance ?? '~',
                4,
              )} ${selectedToken.symbol}`}
              <MaxButton handleClick={handleMaxClick} />
            </StyledBalanceInfo>
          </StyledLabel>
        )}
        <TokenInput
          amount={amount}
          token={selectedToken}
          handleInputChange={handleInputChangeWithReset}
          handleBlur={handleBlur}
          handleOpenTokenList={() => setIsOpenTokenList(true)}
        />
      </StyledFormGroup>

      <StyledReceiveInfo>
        <StyledInfoRow>
          <StyledInfoLabel>{t('order_receive')}</StyledInfoLabel>
          {isReceiveAmountValidating ? (
            <Skeleton />
          ) : (
            <StyledInfoValue>
              <span>{`${
                receiveAmount ? formatFormAmount(receiveAmount) : '~'
              } ETH`}</span>
              <img src={ETH.logo} alt="ETH" width={20} height={20} />
            </StyledInfoValue>
          )}
        </StyledInfoRow>
        <StyledInfoRow>
          <StyledInfoLabel>{t('rate')}</StyledInfoLabel>
          {isRateValidating ? (
            <Skeleton />
          ) : (
            <StyledInfoValue>{`1 ${selectedToken.symbol} = ${
              rate ? formatFormAmount(rate) : '~'
            } ETH`}</StyledInfoValue>
          )}
        </StyledInfoRow>
        <StyledInfoRow>
          <StyledInfoLabel>
            {t('waiting_time')}
            <Tooltip
              title={t('waiting_time_tip')}
              placement="top"
              trigger="hover"
              img={IconQuestionMark}
              iconClassName="tooltip-wrap-new"
            />
          </StyledInfoLabel>
          {isWaitingTimeValidating ? (
            <Skeleton />
          ) : (
            <StyledInfoValue>{waitingTime}</StyledInfoValue>
          )}
        </StyledInfoRow>
        <StyledInfoRow>
          <StyledInfoLabel>{t('estimate_gas_fee')}</StyledInfoLabel>
          {isGasFeeValidating ? (
            <Skeleton />
          ) : (
            <StyledInfoValue>
              {gasFeeUSD ? `~ $${gasFeeUSD}` : '~'}
            </StyledInfoValue>
          )}
        </StyledInfoRow>
      </StyledReceiveInfo>

      {showOrderProgress && <OrderProgress stage={currentStage!} />}
      {!showOrderProgress && (
        <>
          {!isValidNetwork && (
            <NotificationBar
              level="warning"
              text={t('unmatched_network_swap_warning')}
              showLink={false}
              showClose={false}
            />
          )}
          <PrimaryButton
            text={buttonText}
            disabled={buttonDisabled}
            needApprove={showApproveButton}
            loading={currentStage === TransactionCallbackStage.SIGN}
            handleClick={handleClick}
          />
        </>
      )}
    </StyledStakeForm>
  )
}

export default LidoWithdrawalSection
